/**
 * AboutPage Component
 *
 * Static page displaying the MedSuite Product Governance Committee (PGC) Charter.
 * This page provides information about the governance framework, roles, and processes.
 */

import React from 'react';

/**
 * Section component for consistent styling
 */
const Section: React.FC<{
  id: string;
  number: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, number, title, children }) => (
  <section id={id} className="scroll-mt-[180px]">
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-3">
      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 text-sm font-bold">
        {number}
      </span>
      {title}
    </h2>
    <div className="pl-11">{children}</div>
  </section>
);

/**
 * Bullet list component with custom styling
 */
const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, index) => (
      <li key={index} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-500 dark:bg-indigo-400" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/**
 * Definition list component
 */
const DefinitionList: React.FC<{ items: { term: string; definition: string }[] }> = ({ items }) => (
  <dl className="space-y-4">
    {items.map((item, index) => (
      <div key={index} className="border-l-2 border-indigo-200 dark:border-indigo-700 pl-4">
        <dt className="font-semibold text-gray-900 dark:text-gray-100">{item.term}</dt>
        <dd className="mt-1 text-gray-600 dark:text-gray-400">{item.definition}</dd>
      </div>
    ))}
  </dl>
);

/**
 * Role card component for the decision rights table
 */
const RoleCard: React.FC<{
  role: string;
  responsibility: string;
  authority: string;
  highlight?: boolean;
}> = ({ role, responsibility, authority, highlight = false }) => (
  <div
    className={`rounded-lg border p-4 ${
      highlight
        ? 'border-indigo-300 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
    }`}
  >
    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{role}</h4>
    <div className="space-y-2 text-sm">
      <div>
        <span className="text-gray-500 dark:text-gray-400">Responsibility:</span>
        <p className="text-gray-700 dark:text-gray-300">{responsibility}</p>
      </div>
      <div>
        <span className="text-gray-500 dark:text-gray-400">Authority:</span>
        <p className="text-indigo-600 dark:text-indigo-400 font-medium">{authority}</p>
      </div>
    </div>
  </div>
);

/**
 * AboutPage Component
 */
export const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#004581] to-[#03A832] dark:from-[#003366] dark:to-[#028A28]">
        <div className="mx-auto max-w-5xl px-6 py-16 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-white mb-6">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Version 2.0
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Product Governance Committee (PGC) Policy
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Establishing a formal governance framework for disciplined, transparent, and repeatable
            decision-making for the MedSuite product roadmap.
          </p>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="sticky top-0 z-10 bg-white/95 dark:bg-gray-800/95 backdrop-blur border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="mx-auto max-w-5xl px-6">
          <nav className="flex gap-1 overflow-x-auto py-3 text-sm scrollbar-hide">
            {[
              { id: 'purpose', label: 'Purpose' },
              { id: 'scope', label: 'Scope' },
              { id: 'definitions', label: 'Definitions' },
              { id: 'ownership', label: 'Ownership' },
              { id: 'pgc', label: 'PGC' },
              { id: 'decision-rights', label: 'Decisions' },
              { id: 'intake', label: 'Intake' },
              { id: 'structure', label: 'Structure' },
              { id: 'change-control', label: 'Changes' },
              { id: 'cadence', label: 'Cadence' },
            ].map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="flex-shrink-0 rounded-md px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-5xl px-6 py-12">
        <div className="space-y-12">
          {/* Applies To Banner */}
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-amber-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-200">Applies To</h3>
                <p className="mt-1 text-amber-700 dark:text-amber-300">
                  All MedSuite business units, portfolio companies, and shared services functions
                </p>
                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                  <strong>Review Cadence:</strong> Annual (or upon material organizational change)
                </p>
              </div>
            </div>
          </div>

          {/* Section 1: Purpose */}
          <Section id="purpose" number="1" title="Purpose">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              This policy establishes the formal Product Governance framework for MedSuite,
              including the Product Governance Committee (PGC), to ensure disciplined, transparent,
              and repeatable decision-making for the MedSuite product roadmap.
            </p>
            <div className="mt-6">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                The intent of this policy is to:
              </h3>
              <BulletList
                items={[
                  'Create a single, accountable product roadmap',
                  'Balance operational capacity, technical feasibility, regulatory requirements, and commercial value',
                  'Prevent ad-hoc or unilateral roadmap commitments',
                  'Improve delivery predictability, customer outcomes, and retention',
                  "Align product investment with MedSuite's strategic and financial objectives",
                ]}
              />
            </div>
          </Section>

          {/* Section 2: Scope */}
          <Section id="scope" number="2" title="Scope">
            <p className="text-gray-700 dark:text-gray-300 mb-4">This policy governs:</p>
            <BulletList
              items={[
                'Product roadmap intake, prioritization, and approval',
                'Product roadmap change control',
                'Cross-functional decision-making related to product development',
                'Quarterly roadmap commitment and lock',
              ]}
            />
            <p className="text-gray-700 dark:text-gray-300 mt-6 mb-4">
              This policy applies to all product-related initiatives, including but not limited to:
            </p>
            <BulletList
              items={[
                'RCM, EHR/PM, and Case Management products',
                'Platform, infrastructure, and AI-enabled initiatives',
                'Regulatory-driven enhancements',
                'Customer-requested or sales-driven feature requests',
              ]}
            />
          </Section>

          {/* Section 3: Definitions */}
          <Section id="definitions" number="3" title="Definitions">
            <DefinitionList
              items={[
                {
                  term: 'Product Roadmap',
                  definition:
                    'A prioritized, time-bound plan of product initiatives approved for development and delivery.',
                },
                {
                  term: 'Committed Roadmap',
                  definition:
                    'The set of roadmap items approved for delivery within a defined period (typically quarterly). Items on the committed roadmap are immutable except through formal change control.',
                },
                {
                  term: 'Candidate / Discovery Backlog',
                  definition:
                    'A repository of proposed initiatives that have not yet been approved for the committed roadmap.',
                },
                {
                  term: 'Product Governance Committee (PGC)',
                  definition:
                    'A cross-functional leadership body that provides structured input and prioritization recommendations for the product roadmap.',
                },
              ]}
            />
          </Section>

          {/* Section 4: Product Roadmap Ownership */}
          <Section id="ownership" number="4" title="Product Roadmap Ownership">
            <div className="space-y-4">
              <div className="rounded-lg bg-gradient-to-r from-blue-50 to-emerald-50 dark:from-blue-900/30 dark:to-emerald-900/30 border border-blue-200 dark:border-blue-800 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center">
                    <svg
                      className="h-5 w-5 text-indigo-600 dark:text-indigo-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      Product Manager
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Single owner of the product roadmap. Owns synthesis of customer and market
                      insight. Only the Product Manager may commit product work to Engineering or
                      Implementation.
                    </p>
                  </div>
                </div>
              </div>

              <BulletList
                items={[
                  'The Chief Operating Officer (COO) holds executive accountability and final approval authority in partnership with Chief Technology Officer (CTO) for the committed roadmap.',
                  'The CTO owns engineering execution, feasibility, sequencing, and risk impact review.',
                  'The Chief Growth Officer (CGO) owns commercial strategy input, including ICP and win/loss insights.',
                  'Customer Success Director serves as the structured input channel for customer feedback, not a roadmap decision owner.',
                ]}
              />

              <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
                <p className="text-red-800 dark:text-red-200 font-medium">
                  No other function or individual may independently commit MedSuite to product
                  roadmap items.
                </p>
              </div>
            </div>
          </Section>

          {/* Section 5: Product Governance Committee */}
          <Section id="pgc" number="5" title="Product Governance Committee (PGC)">
            <div className="space-y-8">
              {/* 5.1 Purpose */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  5.1 Purpose
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  The PGC exists to provide disciplined, cross-functional input into roadmap
                  prioritization prior to final approval.
                </p>
              </div>

              {/* 5.2 Chairs */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  5.2 Chairs
                </h3>
                <div className="flex gap-4">
                  <span className="inline-flex items-center gap-2 rounded-full bg-indigo-100 dark:bg-indigo-900 px-4 py-2 text-indigo-700 dark:text-indigo-200 font-medium">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                      />
                    </svg>
                    COO
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-indigo-100 dark:bg-indigo-900 px-4 py-2 text-indigo-700 dark:text-indigo-200 font-medium">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                      />
                    </svg>
                    CTO
                  </span>
                </div>
              </div>

              {/* 5.3 Standing Members */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  5.3 Standing Members
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    'Product Manager (Roadmap Owner)',
                    'CGO',
                    'VP, RCM Operations',
                    'Implementation / PMO Lead',
                  ].map((member) => (
                    <div
                      key={member}
                      className="flex items-center gap-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3"
                    >
                      <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <svg
                          className="h-4 w-4 text-gray-600 dark:text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">{member}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 5.4 Advisory Members */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  5.4 Advisory Members (As Needed)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Customer Success Lead',
                    'Compliance / Regulatory SME',
                    'Finance (CFO or delegate)',
                    'Engineering Leads',
                  ].map((member) => (
                    <span
                      key={member}
                      className="rounded-full bg-gray-100 dark:bg-gray-700 px-3 py-1 text-sm text-gray-700 dark:text-gray-300"
                    >
                      {member}
                    </span>
                  ))}
                </div>
              </div>

              {/* 5.5 Voting */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  5.5 Voting
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Equal standing input among members.{' '}
                  <strong className="text-indigo-600 dark:text-indigo-400">
                    COO serves as final tie-breaker
                  </strong>{' '}
                  and approves commitments.
                </p>
              </div>

              {/* 5.6 CEO Role */}
              <div className="rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  5.6 CEO Role in Product Governance
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  <strong>Chief Executive Officer (CEO) — Escalation Authority</strong>
                </p>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  The CEO does not serve as a standing member of the Product Governance Committee
                  and does not function as the routine tie-breaker for roadmap prioritization
                  decisions.
                </p>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  The CEO&apos;s role in product governance is limited to strategic escalation in
                  exceptional cases where:
                </p>
                <BulletList
                  items={[
                    'The PGC and COO are unable to reach alignment after good-faith deliberation',
                    'A roadmap decision presents material company-level strategic, financial, or platform risk',
                    'A decision meaningfully impacts valuation narrative, capital allocation, investor positioning, or long-term platform direction',
                    'There is evidence of systemic leadership misalignment that cannot be resolved at the operational governance level',
                  ]}
                />
                <p className="text-gray-700 dark:text-gray-300 mt-4 mb-3">
                  In such circumstances, the CEO may:
                </p>
                <BulletList
                  items={[
                    'Review the matter',
                    'Evaluate strategic impact',
                    'Provide a final decision or direction',
                  ]}
                />
                <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    The CEO&apos;s involvement is exception-based only and does not alter standard
                    operating authority, which remains vested in the COO and Product Manager through
                    the PGC governance model.
                  </p>
                </div>
              </div>
            </div>
          </Section>

          {/* Section 6: Decision Rights & Responsibilities */}
          <Section id="decision-rights" number="6" title="Decision Rights & Responsibilities">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RoleCard
                role="Product Manager"
                responsibility="Owns roadmap, intake, prioritization model, insight synthesis"
                authority="Single owner of roadmap"
                highlight
              />
              <RoleCard
                role="PGC"
                responsibility="Provides structured input & prioritization recommendations"
                authority="Advisory & consensus voting"
              />
              <RoleCard
                role="COO"
                responsibility="Final approval & delivery accountability; chairs PGC"
                authority="Final approver & tie-breaker"
                highlight
              />
              <RoleCard
                role="CTO"
                responsibility="Confirms technical feasibility & sequencing; owns technical execution; chairs PGC"
                authority="Feasibility authority"
              />
              <RoleCard
                role="CGO"
                responsibility="Validates commercial value and GTM/ICP alignment"
                authority="Commercial authority"
              />
              <RoleCard
                role="Customer Success Director"
                responsibility="Provides structured customer feedback"
                authority="Input only"
              />
              <RoleCard
                role="CEO"
                responsibility="Provides strategic oversight and acts as escalation authority only in exceptional cases"
                authority="Final escalation authority — not a routine tie-breaker"
              />
            </div>
            <div className="mt-6 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-4">
              <p className="text-amber-800 dark:text-amber-200 font-medium">
                The PGC does not approve the roadmap and does not override executive accountability.
              </p>
            </div>
          </Section>

          {/* Section 7: Roadmap Intake & Evaluation */}
          <Section id="intake" number="7" title="Roadmap Intake & Evaluation">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              All roadmap requests must enter via the standard intake template and may not be
              submitted directly to Engineering. Requests must include:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                'Problem definition',
                'Impacted capability',
                'Customer segment',
                'Revenue/retention impact',
                'Regulatory context',
                'Complexity assessment',
                'Operational impact',
                'Dependencies',
                'Risk factors',
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  <svg
                    className="h-4 w-4 text-green-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {item}
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
              <p className="text-red-800 dark:text-red-200">
                <strong>Requests submitted outside this process will not be reviewed.</strong>
              </p>
            </div>
          </Section>

          {/* Section 8: Roadmap Structure */}
          <Section id="structure" number="8" title="Roadmap Structure">
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              MedSuite maintains a two-tier roadmap structure:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border border-green-200 dark:border-green-700 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                    <svg
                      className="h-5 w-5 text-green-600 dark:text-green-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                    Committed Roadmap
                  </h3>
                </div>
                <BulletList
                  items={[
                    'Locked quarterly',
                    'Used for performance measurement and OKRs',
                    'May only be changed through formal change control',
                  ]}
                />
              </div>

              <div className="rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 border border-blue-200 dark:border-blue-700 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                    <svg
                      className="h-5 w-5 text-blue-600 dark:text-blue-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                    Candidate / Discovery Backlog
                  </h3>
                </div>
                <BulletList
                  items={[
                    'All new requests enter here',
                    'Reviewed during PGC sessions',
                    'No delivery commitment implied',
                  ]}
                />
              </div>
            </div>
          </Section>

          {/* Section 9: Change Control */}
          <Section id="change-control" number="9" title="Change Control">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Mid-cycle changes to the committed roadmap require:
            </p>
            <BulletList
              items={[
                'Explicit COO/CTO approval',
                'Identification of an equal or greater trade-off',
                'Documentation in the PGC Decision Log',
                'Communication to the Executive Leadership Team (ELT)',
              ]}
            />
            <div className="mt-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
              <p className="text-red-800 dark:text-red-200 font-bold">
                There are no exceptions to this requirement.
              </p>
            </div>
          </Section>

          {/* Section 10: Operating Cadence */}
          <Section id="cadence" number="10" title="Operating Cadence">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 text-center">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-1">
                  Monthly
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">PGC Working Session</p>
              </div>
              <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 text-center">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-1">
                  Quarterly
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Roadmap Lock</p>
              </div>
              <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 text-center">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-1">
                  Ad-hoc
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Regulatory / Risk Items</p>
              </div>
            </div>
          </Section>

          {/* Section 11: Documentation & Audit Trail */}
          <Section id="documentation" number="11" title="Documentation & Audit Trail">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              All PGC decisions must be recorded in the PGC Decision Log for governance
              transparency, including:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                'Date',
                'Initiative',
                'Decision (Approve / Defer / Reject)',
                'Rationale',
                'Trade-offs',
                'Final approver',
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  <svg
                    className="h-4 w-4 text-indigo-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {item}
                </div>
              ))}
            </div>
            <p className="mt-4 text-gray-600 dark:text-gray-400 text-sm">
              The Decision Log is retained as part of MedSuite&apos;s governance records.
            </p>
          </Section>

          {/* Section 12: Future State */}
          <Section id="future-state" number="12" title="Future State">
            <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 p-6">
              <h3 className="font-semibold text-teal-800 dark:text-teal-200 mb-3">
                Upon appointment of a Chief Product Officer (CPO):
              </h3>
              <BulletList
                items={[
                  'The CPO becomes Chair of the PGC',
                  'The governance framework remains unchanged',
                  'This policy will be updated accordingly',
                ]}
              />
            </div>
          </Section>

          {/* Section 13: Policy Enforcement */}
          <Section id="enforcement" number="13" title="Policy Enforcement">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Failure to adhere to this policy may result in:
            </p>
            <BulletList
              items={['Roadmap item removal', 'Delay of delivery', 'Escalation to the COO and CTO']}
            />
            <div className="mt-6 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-gray-800 dark:text-gray-200 font-medium">
                This policy supersedes any informal or legacy product decision processes.
              </p>
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            MedSuite Product Governance Committee (PGC) Policy &bull; Version 2.0 &bull;{' '}
            {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
